import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useState } from 'react';
import { LoginTemplate, type PublicUser } from '@sbt-web/auth';
import { useViewport } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { createTransaction } from '@sbt-web/network/client';
import { FeatureURI, HTTPStatusCode, NetError, type AdItem } from '@sbt-web/network/types';
import { sendEventToGTMNextPaint } from '@sbt-web/tracking';
import { urnTransformer } from '@sbt-web/utils';
import { FlowDialog } from '@/client/components/Detail/FlowDialog';
import { UsernameDialogAction, UsernameDialogEvent } from '@/client/components/Detail/UsernameDialog';
import parsePrice from '@/client/utilities/parse-price';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import useUser from '../../../contexts/UserContext';
import { AdBuyButtons } from './components/AdBuyButtons';
type flowType = 'unguided' | 'buynow';
export type Props = {
  hideButtons?: boolean;
  item: AdItem;
};
export const AdBuyEvent = {
  OpenOfferDialog: 'subito:AdBuy:OpenOfferDialog',
  OpenCartDialog: 'subito:AdBuy:OpenCartDialog'
};
const AdBuy = t0 => {
  const $ = _c(51);
  const {
    item,
    hideButtons: t1
  } = t0;
  const hideButtons = t1 === undefined ? false : t1;
  const {
    isMobile
  } = useViewport();
  const [flowUrl, setFlowUrl] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const {
    user,
    requestLogin
  } = useUser();
  let t2;
  if ($[0] !== item.urn) {
    t2 = urnTransformer(item.urn);
    $[0] = item.urn;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const itemIdInfo = t2;
  const itemIdForMessaging = `ad${itemIdInfo?.adId}list${itemIdInfo?.contentId}`;
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  let t3;
  if ($[2] !== item.category.friendlyName || $[3] !== item.features || $[4] !== item.subject || $[5] !== itemIdForMessaging) {
    t3 = () => {
      window.dataLayer?.push({
        ecommerce: null
      });
      sendEventToGTMNextPaint("add_to_cart", {
        ecommerce: {
          currency: "EUR",
          items: [{
            item_name: item.subject,
            item_id: itemIdForMessaging,
            price: parsePrice(item.features[FeatureURI.Price]),
            currency: "EUR",
            item_category: item.category.friendlyName
          }]
        }
      });
    };
    $[2] = item.category.friendlyName;
    $[3] = item.features;
    $[4] = item.subject;
    $[5] = itemIdForMessaging;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const trackClickGTM = t3;
  let t4;
  if ($[7] !== item.advertiser.userId || $[8] !== item.urn) {
    t4 = flow => {
      const ownerId = item.advertiser.userId;
      if (ownerId) {
        getOrCreatePulse()?.queueEvent({
          type: "Click",
          object: {
            "@type": "UIElement",
            "@id": "sdrn:subito:content:shipment:element:buy_ad_detail",
            inReplyTo: {
              "@id": `sdrn:subito:classified:${item.urn}`
            },
            label: flow === "buynow" ? "Acquista" : "Fai una Proposta"
          },
          target: {
            id: `sdrn:subito:user:${ownerId}`,
            "@type": "Account",
            accountId: ownerId
          }
        });
      }
    };
    $[7] = item.advertiser.userId;
    $[8] = item.urn;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const trackClick = t4;
  let t5;
  if ($[10] !== requestLogin) {
    t5 = async () => await new Promise(resolve => requestLogin(LoginTemplate.AdBuy, resolve));
    $[10] = requestLogin;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const showLoginDialog = t5;
  let t6;
  if ($[12] !== showLoginDialog || $[13] !== user) {
    t6 = async action => {
      let userId = user?.id;
      if (!user) {
        const u = await showLoginDialog();
        userId = u.id;
      }
      window.dispatchEvent(new CustomEvent(UsernameDialogEvent.CheckBuyerName, {
        detail: {
          action,
          userId
        }
      }));
    };
    $[12] = showLoginDialog;
    $[13] = user;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const showTransactionDialog = t6;
  let t7;
  if ($[15] !== itemIdForMessaging || $[16] !== showLoginDialog) {
    t7 = async forAction => {
      ;
      try {
        const {
          body
        } = await createTransaction(process.env.NEXT_PUBLIC_HADES_BASE_URL, itemIdForMessaging, "ad", forAction === UsernameDialogAction.BuyNow);
        const {
          flowURL
        } = body;
        if (flowURL) {
          const fullURL = new URL(flowURL);
          fullURL.searchParams.set("entrypoint", "adDetail");
          setFlowUrl(fullURL.toString());
          setOpenModal(true);
        }
      } catch (t8) {
        const e = t8;
        if (e instanceof NetError && e.cause.status === HTTPStatusCode.Unauthorized) {
          return showLoginDialog();
        }
      }
    };
    $[15] = itemIdForMessaging;
    $[16] = showLoginDialog;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const openTransactionFlow = t7;
  let t8;
  if ($[18] !== optimizely || $[19] !== trackClick || $[20] !== trackClickGTM) {
    t8 = flow_0 => {
      optimizely?.onReady().then(() => {
        optimizely.track("Click_CTA_Acquista_or_Fai_una_Proposta_from_ad_detail");
      });
      trackClick(flow_0);
      trackClickGTM();
    };
    $[18] = optimizely;
    $[19] = trackClick;
    $[20] = trackClickGTM;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const sendTrackings = t8;
  let t9;
  if ($[22] !== sendTrackings || $[23] !== showTransactionDialog) {
    t9 = () => {
      sendTrackings("unguided");
      showTransactionDialog(UsernameDialogAction.SendOffer);
    };
    $[22] = sendTrackings;
    $[23] = showTransactionDialog;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleOfferClick = t9;
  let t10;
  if ($[25] !== sendTrackings || $[26] !== showTransactionDialog) {
    t10 = () => {
      sendTrackings("buynow");
      showTransactionDialog(UsernameDialogAction.BuyNow);
    };
    $[25] = sendTrackings;
    $[26] = showTransactionDialog;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleBuyClick = t10;
  let t11;
  if ($[28] !== openTransactionFlow) {
    t11 = event => {
      if (event instanceof CustomEvent && (event.detail?.action === UsernameDialogAction.BuyNow || event.detail?.action === UsernameDialogAction.SendOffer)) {
        openTransactionFlow(event.detail.action);
      }
    };
    $[28] = openTransactionFlow;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const handleGetBuyerName = t11;
  let t12;
  let t13;
  if ($[30] !== handleGetBuyerName) {
    t12 = () => {
      window.addEventListener(UsernameDialogEvent.GetBuyerName, handleGetBuyerName);
      return () => {
        window.removeEventListener(UsernameDialogEvent.GetBuyerName, handleGetBuyerName);
      };
    };
    t13 = [handleGetBuyerName];
    $[30] = handleGetBuyerName;
    $[31] = t12;
    $[32] = t13;
  } else {
    t12 = $[31];
    t13 = $[32];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[33] !== handleBuyClick || $[34] !== handleOfferClick) {
    t14 = () => {
      window.addEventListener(AdBuyEvent.OpenOfferDialog, handleOfferClick);
      window.addEventListener(AdBuyEvent.OpenCartDialog, handleBuyClick);
      return () => {
        window.removeEventListener(AdBuyEvent.OpenOfferDialog, handleOfferClick);
        window.removeEventListener(AdBuyEvent.OpenCartDialog, handleBuyClick);
      };
    };
    t15 = [handleBuyClick, handleOfferClick];
    $[33] = handleBuyClick;
    $[34] = handleOfferClick;
    $[35] = t14;
    $[36] = t15;
  } else {
    t14 = $[35];
    t15 = $[36];
  }
  useEffect(t14, t15);
  let t16;
  if ($[37] !== handleBuyClick || $[38] !== handleOfferClick || $[39] !== hideButtons || $[40] !== item || $[41] !== user?.id) {
    t16 = !hideButtons && <AdBuyButtons item={item} userId={user?.id} onBuyNowButtonClick={handleBuyClick} onSendOfferButtonClick={handleOfferClick} />;
    $[37] = handleBuyClick;
    $[38] = handleOfferClick;
    $[39] = hideButtons;
    $[40] = item;
    $[41] = user?.id;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setFlowUrl("");
      setOpenModal(false);
    };
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== flowUrl || $[45] !== isMobile || $[46] !== openModal) {
    t18 = <FlowDialog fullscreen={isMobile} opened={openModal} onCloseIntent={t17} flowUrl={flowUrl} />;
    $[44] = flowUrl;
    $[45] = isMobile;
    $[46] = openModal;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t16 || $[49] !== t18) {
    t19 = <>{t16}{t18}</>;
    $[48] = t16;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
};
export default AdBuy;